import { useEffect, useState } from 'react'

import type { ProductCard } from '../../lib/wstypes'

/**
 * "View in your space" — true spatial AR, where the platform allows it (07 §1, §6).
 *
 * 07 opens with the constraint: **iPhone Safari has no WebXR**, and most homeowners are on
 * iPhones. So this is not the main event — the composited render is — and it appears only when
 * two things are both true: the product has a 3-D model (few do), and the device can place one.
 *
 *  * **iOS** gets AR Quick Look, via a plain `<a rel="ar">` around an image. Safari intercepts
 *    it; no library, no permission prompt, and the USDZ carries the real dimensions so the
 *    vanity is the size it will actually be.
 *  * **Android** gets `<model-viewer ar>`, which uses WebXR where available and Scene Viewer
 *    where not.
 *  * **Everything else** gets a 3-D turntable, which is still better than a product photo.
 *
 * `@google/model-viewer` is loaded on demand: it is ~300 kB and almost no session will show a
 * product that has a model, so paying for it up front would be a tax on everybody for a feature
 * almost nobody reaches.
 */

interface Props {
  product: ProductCard
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string
        'ios-src'?: string
        ar?: boolean
        'ar-modes'?: string
        'camera-controls'?: boolean
        'shadow-intensity'?: string
        alt?: string
        poster?: string
      }
    }
  }
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as a Mac; the touch points are what give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function ViewInSpace({ product }: Props) {
  const [open, setOpen] = useState(false)
  const [ready, setReady] = useState(false)

  const glb = product.model_glb_url
  const usdz = product.model_usdz_url

  useEffect(() => {
    if (!open || ready || isIOS()) return
    let cancelled = false
    void import('@google/model-viewer').then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [open, ready])

  // Nothing to place. The card simply does not offer it (07 §6).
  if (!glb && !usdz) return null

  // iOS: AR Quick Look, straight from the card. No viewer to load.
  if (isIOS() && usdz) {
    return (
      <a
        rel="ar"
        href={usdz}
        className="min-h-11 rounded-btn border border-line bg-white px-3.5 text-sm font-semibold leading-[2.4] text-blue-deep"
      >
        {/* Safari requires an <img> child to intercept the link. */}
        <img src={product.image_url ?? ''} alt="" className="hidden" />
        View in your space
      </a>
    )
  }

  if (!glb) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="min-h-11 rounded-btn border border-line bg-white px-3.5 text-sm font-semibold text-blue-deep"
      >
        View in your space
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-navy-2 on-dark">
          <header className="flex items-center justify-between gap-3 px-5 pb-3 pt-[calc(var(--safe-top)+1rem)]">
            <p className="m-0 max-w-[60%] truncate text-sm font-semibold text-white">
              {product.name}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="min-h-11 rounded-btn border border-white/30 px-4 text-sm font-semibold text-white"
            >
              Close
            </button>
          </header>

          <div className="flex-1">
            {ready ? (
              <model-viewer
                src={glb}
                ios-src={usdz ?? undefined}
                ar
                ar-modes="webxr scene-viewer quick-look"
                camera-controls
                shadow-intensity="1"
                alt={product.name}
                style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
              />
            ) : (
              <p className="grid h-full place-items-center text-sm text-white/70">
                Loading the 3-D view…
              </p>
            )}
          </div>

          <p className="px-5 pb-[calc(var(--safe-bottom)+1rem)] pt-2 text-center text-xs text-white/70">
            Shown at its real size.
          </p>
        </div>
      )}
    </>
  )
}
